import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import {createFilterOptions} from "@mui/material/Autocomplete";

export interface IndustryPropertyOptionItem {
    id: string | number;
    title: string;
}

interface IndustryPropertyOptionsAutocompleteProps<M extends boolean = false> extends Omit<
    AutocompleteProps<IndustryPropertyOptionItem, M, false, false>,
    'renderInput' | 'renderOption' | 'options'
> {
    propertyId: string | number;
    /** Preloaded options (e.g. import transform cache); skips API when provided. */
    cachedOptions?: IndustryPropertyOptionItem[];
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;

    [key: string]: any;
}

const defaultFilterOptions = createFilterOptions<IndustryPropertyOptionItem>({
    ignoreCase: true,
    limit: 100,
    stringify: (option) => option?.title ?? '',
});

const IndustryPropertyOptionsAutocomplete = <M extends boolean = false>({
    propertyId,
    cachedOptions,
    textFieldProps = {},
    form = false,
    name = undefined,
    required = false,
    setLoadingOptions: setLoadingOptionsProps,
    filterOptions = defaultFilterOptions,
    ...props
}: IndustryPropertyOptionsAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        if (cachedOptions?.length) {
            const kw = (keywords ?? '').trim().toLowerCase();
            if (!kw) {
                return cachedOptions;
            }
            return cachedOptions.filter((o) => (o.title ?? '').toLowerCase().includes(kw));
        }
        setLoadingOptions(true);
        try {
            const response = await Api.listIndustryPropertyPropertyValues(propertyId, cancelToken);
            if (!isMounted()) {
                return [];
            }
            if (!response?.resultFlag) {
                return [];
            }
            return (response.data ?? []).map((e) => ({
                id: e.id,
                title: e.content ?? '',
            }));
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [cachedOptions, isMounted, propertyId]);

    const isOptionEqualToValue = useCallback(
        (option: IndustryPropertyOptionItem, value: IndustryPropertyOptionItem | null) =>
            option?.id === value?.id,
        [],
    );

    const getOptionLabel = useCallback((option: IndustryPropertyOptionItem) => option?.title ?? '', []);

    const getOptionKey = useCallback((option: IndustryPropertyOptionItem) => String(option?.id ?? ''), []);

    const staticOptions = useMemo(() => cachedOptions ?? [], [cachedOptions]);

    return (
        <AutocompleteBase<IndustryPropertyOptionItem, M>
            keywordsBased={!cachedOptions?.length}
            canSearchEmptyKeywords={!!cachedOptions?.length}
            getOptions={getOptions}
            timeout={300}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={staticOptions}
            filterOptions={filterOptions}
            dependencies={[propertyId, cachedOptions]}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default IndustryPropertyOptionsAutocomplete;
